import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { ExtensionRelease } from "@edufadder/shared-types";
import { colors, fonts, radii, spacing } from "@edufadder/ui";
import * as api from "../lib/api.js";
import { compareVersions, useExtensionStatus } from "../lib/extension-status.js";
import * as s from "../styles.js";

function CopyText({ text }: { text: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: spacing(2), flexWrap: "wrap" }}>
      <code style={{ background: colors.goldTint, border: `1px solid ${colors.border}`, padding: "2px 8px", borderRadius: radii.sm, fontSize: 14 }}>{text}</code>
      <button
        type="button"
        style={{ ...s.buttonSecondary, padding: "2px 10px", fontSize: 13 }}
        onClick={() => {
          void navigator.clipboard?.writeText(text).then(() => setCopied(true));
        }}
      >
        {copied ? "Copied ✓" : "Copy"}
      </button>
    </span>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }): JSX.Element {
  return (
    <li style={{ display: "flex", gap: spacing(4), marginBottom: spacing(5), listStyle: "none" }}>
      <span
        aria-hidden="true"
        style={{ flexShrink: 0, width: 34, height: 34, borderRadius: 999, background: colors.brand, color: colors.gold, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        {n}
      </span>
      <div>
        <h3 style={{ fontFamily: fonts.serif, fontSize: 18, margin: `${spacing(1)} 0 ${spacing(1)}` }}>{title}</h3>
        <div style={{ lineHeight: 1.6 }}>{children}</div>
      </div>
    </li>
  );
}

export function GetExtensionPage(): JSX.Element {
  const ext = useExtensionStatus();
  const [release, setRelease] = useState<ExtensionRelease | null>(null);

  useEffect(() => {
    api.getExtensionRelease().then(setRelease).catch(() => setRelease(null));
  }, []);

  const latest = release?.latest_version ?? null;
  const outdated = Boolean(ext.installed && ext.version && latest && compareVersions(ext.version, latest) < 0);

  return (
    <div style={{ ...s.wide, maxWidth: 940 }}>
      <h1 style={{ ...s.heading1, fontSize: 34 }}>Get the EduFadder extension</h1>
      <hr style={s.goldRule} />
      <p style={{ ...s.lead, maxWidth: 760 }}>
        Free, for Google Chrome (and other Chromium browsers such as Microsoft Edge and Brave). No account needed. Install it
        once and it notices education ideas as you browse, search, and read.
      </p>

      {/* Live status for this browser */}
      <div
        role="status"
        style={{
          ...s.card,
          borderLeft: `6px solid ${ext.installed ? (outdated ? colors.gold : colors.supportGreen) : colors.border}`,
          marginTop: spacing(6),
        }}
      >
        {!ext.installed && <strong>EduFadder isn't installed in this browser yet.</strong>}
        {ext.installed && !outdated && (
          <>
            <strong style={{ color: colors.supportGreen }}>You have EduFadder v{ext.version}{latest ? " — the latest version ✓" : ""}</strong>
            <div style={s.mutedText}>Automatic detection is {ext.autoDetect === "on" ? "on" : "off — turn it on from the toolbar icon"}.</div>
          </>
        )}
        {outdated && (
          <>
            <strong>You have v{ext.version}. Version {latest} is ready.</strong>
            <div style={s.mutedText}>
              Your notes and evidence are already current — this update adds features. <a href="#update">How to update (2 minutes) ↓</a>
            </div>
          </>
        )}
      </div>

      <div className="ef-grid-2" style={{ alignItems: "center", gap: spacing(8), margin: `${spacing(8)} 0` }}>
        <div>
          <a href={release?.download_url || "/downloads/edufadder-extension.zip"} style={{ ...s.buttonGold, fontSize: 17, padding: `${spacing(4)} ${spacing(7)}` }} download>
            Download EduFadder{latest ? ` v${latest}` : ""} (.zip)
          </a>
          <p style={{ ...s.mutedText, marginTop: spacing(3) }}>
            {release?.released_at ? `Released ${release.released_at}. ` : ""}
            Free, open to inspect, no ads, no tracking.
          </p>
          <p style={{ ...s.mutedText, marginTop: spacing(2) }}>
            Once installed, EduFadder works on its own as you browse. Pages are checked on your device first; only passages
            that mention a known idea are sent, never the page address. You can pause a site, snooze, or turn it off at any
            time. <Link to="/privacy">Privacy details</Link>
          </p>
        </div>
        <img src="/media/edufadder-demo.gif" alt="Animation of EduFadder highlighting an idea on a page and opening a research note." className="ef-animated" style={{ width: "100%", borderRadius: radii.lg, border: `2px solid ${colors.border}` }} />
      </div>

      <section aria-labelledby="install">
        <h2 id="install" style={s.sectionTitle}>Install in about two minutes</h2>
        <hr style={s.goldRule} />
        <ol style={{ padding: 0, margin: 0 }}>
          <Step n={1} title="Download the zip file">
            Use the gold button above.
          </Step>
          <Step n={2} title="Unzip it into a folder you'll keep">
            For example <em>Documents → EduFadder</em>. Chrome runs EduFadder from this folder, so don't delete it afterwards —
            you'll also reuse it for updates.
          </Step>
          <Step n={3} title="Open Chrome's extensions page">
            Type this into the address bar (browsers don't let web pages link to it directly): <CopyText text="chrome://extensions" />
            <div style={s.mutedText}>In Edge it's edge://extensions; in Brave, brave://extensions.</div>
          </Step>
          <Step n={4} title="Switch on “Developer mode”">
            It's the toggle at the top-right of the extensions page.
          </Step>
          <Step n={5} title="Click “Load unpacked” and choose the EduFadder folder">
            Pick the folder that contains the file <code>manifest.json</code>.
          </Step>
          <Step n={6} title="That's it — it's already working">
            A welcome page opens to confirm EduFadder is on. There's nothing to click: just browse. Pages you already had open
            are checked too. <Link to="/practice">Try it on the practice page</Link>.
          </Step>
          <Step n={7} title="Pin it (recommended)">
            Click the puzzle-piece icon next to the address bar and pin EduFadder, so you can see its count and pause it on any
            site.
          </Step>
        </ol>
        <div style={{ ...s.card, background: colors.goldTint }}>
          <strong>Why “Developer mode”?</strong> EduFadder is distributed directly from this site rather than through the Chrome
          Web Store, and Chrome requires Developer mode to load extensions that way. Chrome may occasionally remind you that a
          developer-mode extension is installed — that's expected.
        </div>
      </section>

      <section id="update" aria-labelledby="update-h">
        <h2 id="update-h" style={s.sectionTitle}>Staying up to date — without reinstalling</h2>
        <hr style={s.goldRule} />
        <p style={s.prose}>
          <strong>Most improvements reach you automatically.</strong> New edufads, updated evidence and sources, better trigger
          phrases, and feature switches all come from the EduFadder server — your installed copy picks them up within a few hours,
          with nothing to download.
        </p>
        <p style={s.prose}>
          Only <em>new features</em> need a new version. When one is ready, the EduFadder toolbar popup (and this page) will tell you.
          Updating keeps your settings, paused sites and sign-in:
        </p>
        <ol style={{ ...s.prose, paddingLeft: spacing(6) }}>
          <li>Download the new zip from this page.</li>
          <li>Unzip it <strong>into the same EduFadder folder</strong>, replacing the old files.</li>
          <li>Open the EduFadder toolbar popup and click <strong>Apply update now</strong> (or click the ↻ reload icon on EduFadder's card at chrome://extensions).</li>
        </ol>
        <div style={{ ...s.card, borderLeft: `6px solid ${colors.dangerRed}` }}>
          <strong>Don't click “Remove”.</strong> Removing the extension deletes its settings. Replacing the files and reloading is all
          an update needs — EduFadder keeps the same identity, so everything carries over.
        </div>
        {release && release.notes.length > 0 && (
          <div style={s.card}>
            <h3 style={{ fontFamily: fonts.serif, margin: `0 0 ${spacing(2)}` }}>What's new in v{release.latest_version}</h3>
            <ul style={{ margin: 0, paddingLeft: spacing(5), lineHeight: 1.7 }}>
              {release.notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <section aria-labelledby="trouble">
        <h2 id="trouble" style={s.sectionTitle}>If something isn't working</h2>
        <hr style={s.goldRule} />
        <dl style={{ ...s.prose, margin: 0 }}>
          <dt style={{ fontWeight: 700 }}>Chrome says “Manifest file is missing or unreadable”.</dt>
          <dd style={{ margin: `0 0 ${spacing(4)}` }}>You chose a folder one level too high or too low. Choose the folder that directly contains manifest.json.</dd>
          <dt style={{ fontWeight: 700 }}>Nothing gets highlighted.</dt>
          <dd style={{ margin: `0 0 ${spacing(4)}` }}>
            That's often correct — EduFadder stays quiet on pages that don't mention an idea it knows. Check the toolbar popup:
            automatic detection should say “on”, and the site shouldn't be paused or snoozed. Then visit the{" "}
            <Link to="/practice">practice page</Link>, which always has a few ideas on it.
          </dd>
          <dt style={{ fontWeight: 700 }}>It says it can't check a page.</dt>
          <dd style={{ margin: `0 0 ${spacing(4)}` }}>Browsers don't allow extensions on their own pages (like chrome://) or the extension gallery.</dd>
          <dt style={{ fontWeight: 700 }}>How do I uninstall?</dt>
          <dd style={{ margin: 0 }}>At chrome://extensions, click Remove on EduFadder's card, then delete the folder.</dd>
        </dl>
      </section>
    </div>
  );
}
